/**
 * Snapshot service — captures the current try-on frame as a PNG Blob.
 *
 * Composites the Three.js canvas (AR overlay) on top of the live camera feed
 * by drawing both into an off-screen canvas with the selfie mirror applied,
 * then calls `toBlob()`. Returns `null` when no canvas is available.
 *
 * This is a stateless extraction from ForgeTryonElement.snapshot() so the
 * widget class stays focused on orchestration.
 */

import { showSnapshotToast } from '@mirrably/core/ui';
import type { MediaPort } from '@mirrably/core';

/** Parameters needed to capture a snapshot. */
export interface SnapshotParams {
  /** The AR canvas element to capture. */
  canvas: HTMLCanvasElement;
  /** Render one frame before capture (call scene.render()). */
  renderFrame: () => void;
  /** Emit the 'snapshot' widget event. */
  emitSnapshot: (productId: string | undefined) => void;
  /** Track the snapshot in analytics. */
  trackSnapshot: (productId: string | undefined) => void;
  /** Show the download/share toast. */
  showToast: (blob: Blob) => void;
  /** Product ID for event payload. */
  productId: string | undefined;
}

/**
 * Capture the current try-on frame as a PNG Blob.
 *
 * @returns A PNG Blob of the composite frame, or `null` on failure.
 */
export async function captureSnapshot(params: SnapshotParams): Promise<Blob | null> {
  const { canvas, renderFrame, emitSnapshot, trackSnapshot, showToast, productId } = params;

  const w = canvas.width || 640;
  const h = canvas.height || 480;

  // Render one frame to ensure the canvas has the latest content
  renderFrame();

  // The canvas now contains both the video feed (VideoTexture) and 3D
  // glasses in a single surface. We draw it into an offscreen canvas
  // with the selfie mirror applied (since canvas.toBlob ignores CSS transforms).
  const offscreen = document.createElement('canvas');
  offscreen.width = w;
  offscreen.height = h;
  const ctx = offscreen.getContext('2d');
  if (!ctx) return null;

  // Apply selfie mirror to match what the user sees on screen
  ctx.translate(w, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(canvas, 0, 0, w, h);
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  // Export as a PNG Blob
  const blob = await new Promise<Blob | null>((resolve) => {
    offscreen.toBlob((b) => resolve(b), 'image/png');
  });

  if (blob) {
    emitSnapshot(productId);
    trackSnapshot(productId);
    showToast(blob);
  }

  return blob;
}

/**
 * Create the toast-showing callback for snapshot captures.
 *
 * Factory keeps the snapshot service unaware of specific ModalElements layout.
 */
export function createSnapshotToastHandler(
  snapshotToast: HTMLDivElement,
  cssPrefix: string,
  mediaPort: MediaPort | undefined,
): (blob: Blob) => void {
  return (blob: Blob) => {
    showSnapshotToast(
      snapshotToast,
      blob,
      'product',
      cssPrefix,
      undefined,
      mediaPort as MediaPort | undefined,
    );
  };
}
